/**
 * Form controls — FormField, FormBlock, FormMessage, Input, Select, Textarea,
 * CheckRow.
 *
 * FormField owns the label/hint/error layout and wires `htmlFor`/`aria-describedby`
 * to the control, which the portal's three previous form systems all did by hand
 * (or skipped). The inputs themselves are thin: they exist so no screen has to
 * remember which class makes a field look right.
 */

import { useId } from 'react';
import type {
  InputHTMLAttributes,
  LabelHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import styles from './form.module.css';

interface FormFieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  /** Receives the generated id and the aria wiring. */
  children: (props: { id: string; 'aria-describedby'?: string; 'aria-invalid'?: boolean }) => ReactNode;
  className?: string;
}

export function FormField({ label, hint, error, required, children, className }: FormFieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={[styles.field, className].filter(Boolean).join(' ')}>
      {label && (
        <label htmlFor={id} className={[styles.label, required ? styles.required : null].filter(Boolean).join(' ')}>
          {label}
        </label>
      )}
      {children({ id, 'aria-describedby': describedBy, 'aria-invalid': error ? true : undefined })}
      {error && (
        <span id={errorId} className={styles.error}>
          {error}
        </span>
      )}
      {hint && !error && (
        <span id={hintId} className={styles.hint}>
          {hint}
        </span>
      )}
    </div>
  );
}

/**
 * A labelled block that is not a single form control — a photo picker, a list of
 * checkboxes, a loading state. It renders the same label and spacing as
 * FormField but a plain <span>, because there is no one input to point `htmlFor`
 * at and a dangling `htmlFor` is worse than none.
 */
export function FormBlock({
  label,
  hint,
  required,
  className,
  children,
}: {
  label?: ReactNode;
  hint?: ReactNode;
  required?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={[styles.field, className].filter(Boolean).join(' ')}>
      {label && (
        <span className={[styles.label, required ? styles.required : null].filter(Boolean).join(' ')}>
          {label}
        </span>
      )}
      {children}
      {hint && <span className={styles.hint}>{hint}</span>}
    </div>
  );
}

/**
 * The mandatory-field marker, for labels too composite to use `required`.
 *
 * `required` appends the asterisk after the *whole* label, which is wrong when
 * the label carries a trailing counter or badge — it lands after the counter.
 * Placing this inline keeps the marker next to the field name and, unlike a
 * hand-typed `*`, keeps it the same colour as the 19 that use `required`.
 */
export function RequiredMark() {
  return <span className={styles.requiredMark}> *</span>;
}

const MESSAGE_TONES = {
  error: styles.messageError,
  success: styles.messageSuccess,
  info: styles.messageInfo,
} as const;

/**
 * A block-level message about a whole form or page — a submit failure, a
 * "saved" confirmation, an expired session. With a `title`, the title leads
 * and the words beneath it say why, if there are any: a failure says what did
 * not happen, then the words that explain it. A failure (the error tone) is
 * role="alert", read out as it comes.
 *
 * Not to be confused with FormField's `error`, which is the small line of text
 * belonging to one control.
 */
export function FormMessage({
  tone = 'error',
  title,
  className,
  children,
}: {
  tone?: keyof typeof MESSAGE_TONES;
  title?: ReactNode;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <div className={[styles.message, MESSAGE_TONES[tone], className].filter(Boolean).join(' ')} role={tone === 'error' ? 'alert' : undefined}>
      {title ? (
        <>
          <p className={styles.messageTitle}>{title}</p>
          {children ? <p className={styles.messageDetail}>{children}</p> : null}
        </>
      ) : (
        children
      )}
    </div>
  );
}

/** Compose the shared control shell with a variant and any caller class. */
function control(variant: string | null, invalid: boolean | undefined, className: string | undefined) {
  return [styles.control, variant, invalid ? styles.invalid : null, className].filter(Boolean).join(' ');
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
  /** Monospace — ids, tokens, JSON snippets. */
  mono?: boolean;
}

export function Input({ invalid, mono, className, ...rest }: InputProps) {
  return <input className={control(mono ? styles.mono : null, invalid, className)} {...rest} />;
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}

export function Select({ invalid, className, children, ...rest }: SelectProps) {
  return (
    <select className={control(styles.select, invalid, className)} {...rest}>
      {children}
    </select>
  );
}

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
  mono?: boolean;
}

export function Textarea({ invalid, mono, className, ...rest }: TextareaProps) {
  const variant = [styles.textarea, mono ? styles.mono : null].filter(Boolean).join(' ');
  return <textarea className={control(variant, invalid, className)} {...rest} />;
}

/**
 * A checkbox on its own — a table's select-row column, a filter list.
 *
 * Exists because `.checkRow input` could only reach a box inside a row, so a
 * bare `<input type="checkbox">` fell back to the UA default: a pale square
 * that reads as disabled on the dark ground.
 */
export function Checkbox({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      type="checkbox"
      className={[styles.check, className].filter(Boolean).join(' ')}
      {...rest}
    />
  );
}

interface CheckRowProps extends LabelHTMLAttributes<HTMLLabelElement> {
  /**
   * Add the same bottom margin a FormField carries. Set this when the row
   * stands on its own as a form field; leave it off when the row is one item in
   * a list, where the container's `gap` or separators own the spacing.
   */
  spaced?: boolean;
  children: ReactNode;
}

/** Checkbox or radio with its label — wrap the input as a child. */
export function CheckRow({ spaced, className, children, ...rest }: CheckRowProps) {
  const classes = [styles.checkRow, spaced ? styles.checkRowSpaced : null, className];
  return (
    <label className={classes.filter(Boolean).join(' ')} {...rest}>
      {children}
    </label>
  );
}
